/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { formatPrice } from '../../utils';
import DriverDropdown from '../../components/dropdowns/driverDropdown';
import TimeDropdown from '../../components/dropdowns/timeDropdown';
import ReportService from '../../services/reportService';
import PageDropdown from '../../components/dropdowns/pageDropdown';
import CityDropdown from '../../components/dropdowns/cityDropdown';
import StreetDropdown from '../../components/dropdowns/streetDropdown';
import PdfProfitReport from '../../components/prints/report/pdfProfitReport';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import { AuthContext } from '../../provider/authProvider';

const ProfitReport = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [reports, setReports] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalProfit, setTotalProfit] = useState(0);
    const [totalPosts, setTotalPosts] = useState(0);

    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedStreet, setSelectedStreet] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });

    const [isLoading, setIsLoading] = useState(false);

    const fetchReport = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                branchId: selectedBranch?.id,
                driverId: selectedDriver?.id,
                pageId: selectedPage?.id,
                streetId: selectedStreet?.id,
                cityId: selectedCity?.id,
                ...selectedTime
            };

            const data = await ReportService.fetchProfit(filters);
            const { reports, totalPrice, totalProfit, totalCount } = data;

            setTotalPrice(totalPrice);
            setTotalProfit(totalProfit);
            setTotalPosts(totalCount);
            setReports(reports);

        } catch (error) {
            console.error('Error fetching report:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');


    const resetFilter = () => {
        setSelectedPage(null);
        setSelectedDriver(null);
        setSelectedCity(null);
        setSelectedStreet(null);
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
    };


    useEffect(() => {
        fetchReport();
    }, [selectedBranch, selectedPage, selectedDriver, selectedCity, selectedStreet, selectedTime]);

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>

            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            width: '100%'
                        }}
                    >

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                        }}>
                            <Box sx={{ px: 2, }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'90%'}>
                                <CityDropdown
                                    isFilter={true}
                                    selectedCity={selectedCity}
                                    setSelectedCity={setSelectedCity}
                                />
                            </Box>
                        </Box>
                        <Divider />

                        {allBranchPermission && <Box>
                            <Box sx={{ width: '100%' }}>
                                <BranchDropdown
                                    isFilter={true}
                                    selectedBranch={selectedBranch}
                                    setSelectedBranch={setSelectedBranch}
                                />
                            </Box>
                            <Divider />
                        </Box>}

                        <Box width={'100%'}>
                            <StreetDropdown
                                isFilter={true}
                                selectedStreet={selectedStreet}
                                setSelectedStreet={setSelectedStreet}
                                cityId={selectedCity && selectedCity.id}
                            />
                        </Box>

                        <Divider />
                        <Box width={'100%'}>
                            <PageDropdown
                                isFilter={true}
                                selectedPage={selectedPage}
                                setSelectedPage={setSelectedPage}
                            />
                        </Box>
                        <Divider />
                        <Box width={'100%'}>
                            <DriverDropdown
                                isFilter={true}
                                selectedDriver={selectedDriver}
                                setSelectedDriver={setSelectedDriver}
                            />
                        </Box>

                        <Divider />
                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>

                        <Divider />

                        <Box width={'100%'} my={1}>
                            <Typography variant="body2" sx={{ mx: 1 }}>
                                {t('totalPrice')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPrice)}</span>
                            </Typography>
                        </Box>
                        <Divider />
                        <Box width={'100%'} my={1}>
                            <Typography variant="body2" sx={{ mx: 1 }}>
                                {t('totalProfit')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalProfit)}</span>
                            </Typography>
                        </Box>

                        <Divider />
                        <Box width={'100%'} my={1}>
                            <Typography variant="body2" sx={{ mx: 1 }}>
                                {t('totalPosts')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPosts)}</span>
                            </Typography>
                        </Box>

                        <Divider />
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: '30%'
                                }}
                            >
                                <PdfProfitReport
                                    data={{
                                        city: selectedCity,
                                        street: selectedStreet,
                                        page: selectedPage,
                                        driver: selectedDriver,
                                        branch: selectedBranch,
                                        time: selectedTime,
                                    }} />
                            </Box>

                            <Divider orientation="vertical" />
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                    width: '60%'
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>

                    </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{ px: 2, }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" sx={{ height: '101px' }} />
                        <Box sx={{
                            width: '100%'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50,
                            }}>
                                {allBranchPermission && <Box width={'20%'}>
                                    <BranchDropdown
                                        isFilter={true}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                    />
                                </Box>}
                                {allBranchPermission && <Divider orientation="vertical" />}
                                <Box width={allBranchPermission ? '20%' : '25%'}>
                                    <CityDropdown
                                        isFilter={true}
                                        selectedCity={selectedCity}
                                        setSelectedCity={setSelectedCity}
                                    />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={allBranchPermission ? '20%' : '25%'}>
                                    <StreetDropdown
                                        isFilter={true}
                                        selectedStreet={selectedStreet}
                                        setSelectedStreet={setSelectedStreet}
                                        cityId={selectedCity && selectedCity.id}
                                    />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={allBranchPermission ? '20%' : '25%'}>
                                    <PageDropdown
                                        isFilter={true}
                                        selectedPage={selectedPage}
                                        setSelectedPage={setSelectedPage}
                                    />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={allBranchPermission ? '20%' : '25%'}>
                                    <DriverDropdown
                                        isFilter={true}
                                        selectedDriver={selectedDriver}
                                        setSelectedDriver={setSelectedDriver}
                                    />
                                </Box>
                            </Box>

                            <Divider />
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50
                            }}>

                                <Box width={'40%'}>
                                    <TimeDropdown
                                        isFilter={true}
                                        selectedTime={selectedTime}
                                        setSelectedTime={setSelectedTime}
                                    />
                                </Box>

                                <Divider orientation="vertical" />

                                <Box width={'15%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalPrice')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPrice)}</span>
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={'15%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalProfit')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalProfit)}</span>
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={'14%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalPosts')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPosts)}</span>
                                    </Typography>
                                </Box>

                                <Divider orientation="vertical" />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: '4%'
                                    }}
                                >
                                    <PdfProfitReport
                                        data={{
                                            city: selectedCity,
                                            street: selectedStreet,
                                            page: selectedPage,
                                            driver: selectedDriver,
                                            branch: selectedBranch,
                                            time: selectedTime,
                                        }} />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: theme.palette.text.red,
                                        px: 2,
                                        cursor: 'pointer',
                                    }}
                                    onClick={resetFilter}
                                >
                                    <ReplayOutlinedIcon />
                                    <Box width={2}></Box>
                                    <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 210px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>{t('date')}</TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>{t('price')}</TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>{t('profit')}</TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>{t('posts')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports.map((report, index) => (
                            <TableRow
                                key={report.date} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'}>{report.date}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(report.price)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(report.profit)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(report.count)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ProfitReport;
